// frontend/src/pages/booking/TourDetailPage.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import API from '../../services/api';
import ResponsiveImage from '../../components/ui/ResponsiveImage';

const TourDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: tour, loading } = useApi(() => API.tours.getBySlug(slug));
  const [showBookingModal, setShowBookingModal] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Tour Not Found</h1>
          <button
            onClick={() => navigate('/tours')}
            className="px-6 py-3 bg-emerald-600 rounded-lg hover:bg-emerald-700"
          >
            Back to Tours
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white pt-24">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImage
            src={tour.image_url}
            alt={tour.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-4 flex flex-col justify-end pb-12">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {tour.tags?.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-emerald-600/80 backdrop-blur-sm rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-5xl md:text-6xl font-playfair font-bold">
              {tour.name}
            </h1>
            {tour.subtitle && (
              <p className="text-2xl text-gray-300">{tour.subtitle}</p>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div>
              <h2 className="text-3xl font-playfair font-bold mb-4">About This Tour</h2>
              <div
                className="text-gray-300 space-y-4 prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: tour.long_description }}
              />
            </div>

            {/* What's Included */}
            {tour.includes && tour.includes.length > 0 && (
              <div>
                <h2 className="text-3xl font-playfair font-bold mb-4">What's Included</h2>
                <ul className="grid md:grid-cols-2 gap-3">
                  {tour.includes.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pickup Locations */}
            {tour.pickup_locations && tour.pickup_locations.length > 0 && (
              <div>
                <h2 className="text-3xl font-playfair font-bold mb-4">Pickup Locations</h2>
                <ul className="space-y-2">
                  {tour.pickup_locations.map((location, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-gray-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {location}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Gallery */}
            {tour.gallery && tour.gallery.length > 0 && (
              <div>
                <h2 className="text-3xl font-playfair font-bold mb-4">Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {tour.gallery.map((img, idx) => {
                    // Construct URL using the same logic as backend's getImageUrl()
                    const imageUrl = typeof img === 'string'
                      ? `${window.location.origin}/backend/uploads/tours/${img}`
                      : img;

                    return (
                      <div key={idx} className="aspect-square overflow-hidden rounded-lg">
                        <ResponsiveImage
                          src={imageUrl}
                          alt={`${tour.name} gallery ${idx + 1}`}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-gray-800 rounded-2xl p-6 space-y-6 border border-gray-700">
              <div>
                <div className="border-b border-gray-700 pb-6">
                  <div className="text-sm text-gray-400 mb-2">Starting from</div>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-4xl font-bold text-emerald-500">
                      ${tour.price_single_atv || tour.price}
                    </span>
                    <span className="text-gray-400">USD</span>
                  </div>

                  {/* ATV Pricing Breakdown */}
                  <div className="space-y-3 bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl"></span>
                        <div>
                          <p className="font-bold text-white">Single Ride</p>
                          <p className="text-xs text-gray-400">Solo rider experience</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-emerald-400">
                        ${tour.price_single_atv || tour.price}
                      </span>
                    </div>

                    <div className="border-t border-gray-600 pt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl"></span>
                        <div>
                          <p className="font-bold text-white">Shared Ride</p>
                          <p className="text-xs text-gray-400">Rider + passenger (2 people)</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-emerald-400">
                       ${tour.price_double_atv || tour.shared_ride_fee || (tour.price * 1.5).toFixed(0)}
                      </span>
                    </div>

                    {/* <div className="text-xs text-gray-400 italic pt-2 border-t border-gray-600">
                      💡 Passengers ride FREE on double-seaters
                    </div> */}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {/* Duration */}
                <div className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{tour.duration}</span>
                </div>

                {/* Max Participants */}
                {/* <div className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Up to {tour.max_participants} participants</span>
                </div> */}

                {/* Min Age */}
                {tour.min_age > 0 && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Minimum age: {tour.min_age} years</span>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowBookingModal(true)}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105"
              >
                Book Now
              </button>

              {/* <p className="text-xs text-gray-400 text-center">
                Free cancellation up to 24 hours before the tour
              </p> */}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal
          tour={tour}
          onClose={() => setShowBookingModal(false)}
        />
      )}
    </div>
  );
};

// Booking Modal Component
const BookingModal = ({ tour, onClose }) => {
  const navigate = useNavigate();

  const handleStartBooking = () => {
    // Store tour in sessionStorage and navigate
    sessionStorage.setItem('selectedTour', JSON.stringify(tour));
    navigate(`/booking/new`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-2xl p-8 max-w-lg w-full space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-playfair font-bold mb-2">Start Your Booking</h2>
          <p className="text-gray-400">You're just a few steps away from your adventure!</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center font-bold">1</div>
            <div>
              <h3 className="font-bold">Select Date & Time</h3>
              <p className="text-sm text-gray-400">Choose your preferred slot</p>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center font-bold">2</div>
            <div>
              <h3 className="font-bold">Participant Details</h3>
              <p className="text-sm text-gray-400">Add traveler information</p>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center font-bold">3</div>
            <div>
              <h3 className="font-bold">Sign Waivers</h3>
              <p className="text-sm text-gray-400">Digital signature required</p>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center font-bold">4</div>
            <div>
              <h3 className="font-bold">Payment</h3>
              <p className="text-sm text-gray-400">Secure checkout with Stripe</p>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleStartBooking}
            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-bold transition-colors"
          >
            Let's Go!
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourDetailPage;